import * as React from 'react';
import { Icon } from '../icons/Icons';
import { TextArea, TextAreaProps } from './TextArea';
import { TextWithSaveButtonRow, TextWithSaveCancelButton, TextWithSaveContainer, TextWithSaveSaveButton } from './TextInputWithSave';

// IMPORTANT! See comments in TextInput version


export type TextAreaWithSaveProps = Omit<TextAreaProps, 'onChange'> & {
    onChange?: (newValue:string) => void
    onCancel?: (cancelledValue:string, restoredValue:string) => void
    onSave: (newValue: string) => void,
    onSaveNoChange?:()=>void
}

export function TextAreaWithSave(props:TextAreaWithSaveProps): JSX.Element {
    const [thisValue, setThisValue] = React.useState(props.value);
    const [hasFocus, setHasFocus] = React.useState(false);

    const cancelButtonRef = React.useRef(null);
    const saveButtonRef = React.useRef(null);

    React.useEffect(() => {
        setThisValue(props.value);
    }, [props.value]);

    function save() {
        // only save if value has changed
        if (thisValue != props.value)
            props.onSave(thisValue);
        else if(props.onSaveNoChange)
            props.onSaveNoChange();
    }

    function handleTextFocus(/*e:React.FocusEvent<HTMLInputElement>*/) {
        setHasFocus(true);
    }

    function handleTextBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
        const isCancel = e.relatedTarget === cancelButtonRef.current;
        const isSave = e.relatedTarget === saveButtonRef.current;

        setHasFocus(false);

        if (isCancel || isSave) {
            // Do nothing yet.
            // If cancel is eventually pressed, it will be handled by the handler
        }
        else {
            // any non-cancel click is implicitly saving
            // only save if value changed and isValid
            save();
        }
    }

    function handleCancelClick() {
        if (thisValue != props.value){    
            setThisValue(props.value);
        }
        if(props.onCancel)
            props.onCancel(thisValue, props.value);
    }

    function handleSaveClick() {
        save();
    }

    function handleChange(newValue:string) {
        setThisValue(newValue);
        if (props.onChange)
            props.onChange(newValue);
    }

    return (
        <TextWithSaveContainer>

            <TextArea {...props}
                value={thisValue}
                onChange={handleChange}
                onFocus={handleTextFocus}
                onBlur={handleTextBlur} />

            <TextWithSaveButtonRow style={{ visibility: hasFocus ? "visible" : "hidden" }}>

                <TextWithSaveCancelButton
                    ref={cancelButtonRef}
                    tabIndex={1}
                    onClick={handleCancelClick}>
                    <Icon name={"actions_close"} size={"small"} />
                </TextWithSaveCancelButton>

                <TextWithSaveSaveButton
                    ref={saveButtonRef}
                    tabIndex={2}
                    onClick={handleSaveClick}>
                    <Icon name={"actions_done"} size={"small"} />
                </TextWithSaveSaveButton>

            </TextWithSaveButtonRow>

        </TextWithSaveContainer>
    );
}